import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import * as customersApi from '@/api/customers'
import { sidebarNavClass } from '@/lib/sidebarStyles'
import {
  customerKbPath,
  getRememberedSettingsCustomer,
  getRememberedSettingsCustomerName,
  rememberSettingsCustomer,
  settingsOverviewPath,
} from '@/settingsContext'

function isSettingsSection(pathname) {
  return (
    pathname === '/settings' ||
    pathname === '/settings/' ||
    pathname.startsWith('/settings/') ||
    pathname === '/users' ||
    pathname === '/manage-customers' ||
    pathname === '/manage-categories' ||
    pathname === '/ai-search' ||
    pathname === '/expiries'
  )
}

/**
 * Collapsible admin Settings block for the dark left menu.
 * @param {{ customerId?: string|number, customerName?: string }} props
 */
export function SettingsNav({ customerId, customerName }) {
  const location = useLocation()
  const [rememberedId, setRememberedId] = useState(() => getRememberedSettingsCustomer())
  const [rememberedName, setRememberedName] = useState(() =>
    getRememberedSettingsCustomerName(),
  )
  const inSettings = isSettingsSection(location.pathname)
  const [open, setOpen] = useState(inSettings)

  useEffect(() => {
    if (customerId != null && customerId !== '') {
      rememberSettingsCustomer(customerId, customerName)
      setRememberedId(String(customerId))
      if (customerName) setRememberedName(customerName)
    } else {
      setRememberedId(getRememberedSettingsCustomer())
      setRememberedName(getRememberedSettingsCustomerName())
    }
  }, [customerId, customerName])

  useEffect(() => {
    if (inSettings) setOpen(true)
  }, [inSettings])

  const activeCustomer = customerId != null ? String(customerId) : rememberedId
  const displayName = customerName || rememberedName

  useEffect(() => {
    if (!activeCustomer || displayName) return undefined
    let cancelled = false
    async function loadName() {
      try {
        const c = await customersApi.getCustomer(activeCustomer)
        if (!cancelled && c?.name) {
          rememberSettingsCustomer(activeCustomer, c.name)
          setRememberedName(c.name)
        }
      } catch {
        // ignore
      }
    }
    loadName()
    return () => {
      cancelled = true
    }
  }, [activeCustomer, displayName])

  const overviewTo = settingsOverviewPath(activeCustomer)
  const kbTo = customerKbPath(activeCustomer)
  const kbLabel = displayName
    ? `Back to ${displayName}`
    : 'Back to knowledge base'

  const onOverview =
    location.pathname === '/settings' || location.pathname === '/settings/'
  const onFieldNames =
    location.pathname === '/settings/field-names' ||
    location.pathname === '/settings/site'
  const onSecurity = location.pathname === '/settings/security'
  const onAttachments = location.pathname === '/settings/attachments'
  const onBackups = location.pathname === '/settings/backups'
  const onAi = location.pathname === '/settings/ai'
  const onAiSearch = location.pathname === '/ai-search'
  const onExpiries = location.pathname === '/expiries'
  const onExpirySettings = location.pathname === '/settings/expiries'
  const onTelegram = location.pathname === '/settings/telegram'
  const onLoginActivity = location.pathname === '/settings/login-activity'
  const onCustomers = location.pathname === '/manage-customers'
  const onUsers = location.pathname === '/users'
  const onCategories = location.pathname === '/manage-categories'
  const onTemplates = location.pathname.startsWith('/settings/templates')

  return (
    <div>
      {kbTo && inSettings && (
        <div className="space-y-1 border-b border-sidebar-border p-3">
          <Link
            to={kbTo}
            className="block rounded-md bg-white/10 px-3 py-2 text-sm font-semibold text-white hover:bg-white/15"
          >
            {kbLabel}
          </Link>
          <Link
            to="/customers"
            className="block rounded-md px-3 py-1.5 text-xs text-sidebar-foreground/60 hover:bg-white/8 hover:text-white"
          >
            All customers
          </Link>
        </div>
      )}
      <div className="p-2">
        <div className="mb-1 flex items-center justify-between px-3 py-1">
          <Link
            to={overviewTo}
            className="text-xs font-medium uppercase tracking-wider text-sidebar-foreground/50 hover:text-white"
          >
            Settings
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-xs text-sidebar-foreground/50 hover:text-white"
            aria-expanded={open}
            aria-label={open ? 'Hide settings menu' : 'Show settings menu'}
          >
            {open ? 'Hide' : 'Show'}
          </button>
        </div>
        {open && (
          <div className="space-y-0.5">
            <NavLink to={overviewTo} className={() => sidebarNavClass({ isActive: onOverview })}>
              Overview
            </NavLink>
            <NavLink
              to="/settings/field-names"
              className={() => sidebarNavClass({ isActive: onFieldNames })}
            >
              Field names
            </NavLink>
            <NavLink
              to="/settings/security"
              className={() => sidebarNavClass({ isActive: onSecurity })}
            >
              Security
            </NavLink>
            <NavLink
              to="/settings/login-activity"
              className={() => sidebarNavClass({ isActive: onLoginActivity })}
            >
              Login activity
            </NavLink>
            <NavLink
              to="/settings/attachments"
              className={() => sidebarNavClass({ isActive: onAttachments })}
            >
              Attachments
            </NavLink>
            <NavLink
              to="/settings/backups"
              className={() => sidebarNavClass({ isActive: onBackups })}
            >
              Backups
            </NavLink>
            <NavLink
              to="/settings/ai"
              className={() => sidebarNavClass({ isActive: onAi })}
            >
              AI
            </NavLink>
            <NavLink
              to="/ai-search"
              className={() => sidebarNavClass({ isActive: onAiSearch })}
            >
              AI Search
            </NavLink>
            <NavLink
              to="/expiries"
              className={() => sidebarNavClass({ isActive: onExpiries })}
            >
              ToDo / Expiring
            </NavLink>
            <NavLink
              to="/settings/expiries"
              className={() => sidebarNavClass({ isActive: onExpirySettings })}
            >
              Expiry warning
            </NavLink>
            <NavLink
              to="/settings/telegram"
              className={() => sidebarNavClass({ isActive: onTelegram })}
            >
              Telegram
            </NavLink>
            <NavLink
              to="/manage-customers"
              className={() => sidebarNavClass({ isActive: onCustomers })}
            >
              Customers
            </NavLink>
            <NavLink to="/users" className={() => sidebarNavClass({ isActive: onUsers })}>
              Users
            </NavLink>
            <NavLink
              to="/manage-categories"
              className={() => sidebarNavClass({ isActive: onCategories })}
            >
              Manage categories
            </NavLink>
            <NavLink
              to="/settings/templates"
              className={() => sidebarNavClass({ isActive: onTemplates })}
            >
              Templates
            </NavLink>
          </div>
        )}
      </div>
    </div>
  )
}
